import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import ReactTooltip from "react-tooltip";
import { t } from "ttag";

import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import iconQuestionMarkCircleFilled from "../../../../img/icons/question-mark-circle-filled.svg";

import styles from "./index.module.scss";

function getItemsLabel(qty: number): string {
    // TODO(i18n): Use ngettext for plurals: ngettext(msgid`item`, `items`, qty)
    return qty === 1 ? "item" : "items";
}

function buildCartReferenceNumber(
    hideBasketID: boolean,
    basketID: string | undefined,
    phoneLink: string | null,
    phoneNumber: string | null,
    isMobile: boolean,
    isKeyDown: boolean,
    keyDownHandler: (event: React.KeyboardEvent<HTMLElement>) => void,
    setIsKeyDown: (value: boolean) => void,
): React.ReactNode {
    if (hideBasketID) {
        return null;
    }
    const tipId = "basket-lines__tooltip-cart-ref";
    return (
        <div className="basket-lines__header-cart-ref">
            <div className="basket-lines__header-cart-ref--first-line">
                {t`Cart Reference Number:`}
            </div>
            <div className="basket-lines__header-cart-ref--second-line">
                <span className="basket-lines__header-cart-ref--bold">
                    {basketID}
                </span>
                {phoneLink && phoneNumber && (
                    <div className="tooltip-container">
                        <div
                            tabIndex={0}
                            aria-describedby={tipId}
                            className="basket-lines__header-cart-ref-icon-container"
                            data-delay-update={100}
                            data-iscapture="true"
                            data-tip={true}
                            data-for={tipId}
                            data-delay-hide={200}
                            title={t`Have an issue completing your order?`}
                            onKeyDown={keyDownHandler}
                            onMouseOver={() => {
                                setIsKeyDown(false);
                            }}
                        >
                            <SVG
                                aria-hidden="true"
                                className="basket-lines__header-cart-ref-icon"
                                src={iconQuestionMarkCircleFilled}
                            />
                            <span className="ada-screenreader-only">
                                {/* TODO(i18n): Use t tagged template: {t`Open Tooltip`} */}
                                Open Tooltip
                            </span>
                        </div>
                        <ReactTooltip
                            aria-label={t`Question about your order?`}
                            class="basket-lines__tooltip basket-lines__tooltip--cart-ref tooltip"
                            clickable={true}
                            event={"mouseover focus"}
                            eventOff="scroll mousewheel mouseleave mouseout blur"
                            id={tipId}
                            uuid={tipId}
                            isCapture={true}
                            place={isMobile ? "left" : "bottom"}
                            role="tooltip"
                        >
                            <strong>
                                {t`Have an issue completing your order?`}
                            </strong>
                            <br />
                            {richT(
                                t`Call <PhoneNumber /> and provide your cart ID for assistance.`,
                                {
                                    PhoneNumber: () => <>{phoneNumber}</>,
                                },
                            )}
                        </ReactTooltip>
                    </div>
                )}
            </div>
        </div>
    );
}

export const BasketHeader = (
    props: Immutable<{
        hideBasketID: boolean;
        phoneLink: string | null;
        phoneNumber: string | null;
        basketID?: string;
        isMobile: boolean;
        totalItems: number;
    }>,
): React.ReactNode => {
    const [isKeyDown, setIsKeyDown] = React.useState(false);

    const keyDownHandler = (event: React.KeyboardEvent<HTMLElement>) => {
        switch (event.key) {
            case "Escape":
                ReactTooltip.hide();
                break;
            case "Enter":
                if (!isKeyDown) {
                    setIsKeyDown(true);
                    return;
                }
                ReactTooltip.hide();
                break;
        }
    };

    return (
        <header className={styles.header}>
            <h1 className={styles.title}>
                {/* TODO(i18n): Use ngettext to keep count and item label together: {ngettext(msgid`Your Cart (${props.totalItems} item)`, `Your Cart (${props.totalItems} items)`, props.totalItems)} */}
                {t`Your Cart`}{" "}
                <span className={styles.itemNumber}>
                    ({props.totalItems} {getItemsLabel(props.totalItems)})
                </span>
            </h1>
            {buildCartReferenceNumber(
                props.hideBasketID,
                props.basketID,
                props.phoneLink,
                props.phoneNumber,
                props.isMobile,
                isKeyDown,
                keyDownHandler,
                setIsKeyDown,
            )}
        </header>
    );
};
